import pRetry from 'p-retry';
import type { WebDriver } from '../webdriver';

export class ScreenshotUtils {
    public readonly screencastHandler = undefined;
    private currentDevicePixelRatio?: number;

    constructor(private readonly tabId: string, private readonly driver: WebDriver, private readonly options: { takeScreenshots?: boolean } = { takeScreenshots: true }) {
    }

    private base64AddPadding(str: string) {
        return str + Array(((4 - (str.length % 4)) % 4) + 1).join('=');
    }

    private shouldTakeScreenshots() {
        if (typeof this.options.takeScreenshots !== 'boolean') {
            return true;
        }
        return this.options.takeScreenshots;
    }

    public async takeScreenshot() {
        if (!this.shouldTakeScreenshots()) {
            return { devicePixelRatio: 1, image: '' };
        }
        const MAX_RETRY_COUNT = 3;
        const SCREENSHOT_RETRY_DELAY = 2000;
        const devicePixelRatioPromise = this.currentDevicePixelRatio || this.getDevicePixelRatio();
        const getScreenshot = () => Promise.all([devicePixelRatioPromise, this.driver.takeScreenshot()]);
        const [devicePixelRatio, image] = await pRetry(getScreenshot, { retries: MAX_RETRY_COUNT, minTimeout: SCREENSHOT_RETRY_DELAY, factor: 1 });
        const base64 = image ? image.value : '';
        const dataUrl = `data:image/png;base64,${this.base64AddPadding(base64.replace(/[\r\n]/g, ''))}`;
        return {
            image: dataUrl,
            devicePixelRatio,
        };
    }

    public async takeElementScreenshot(elem: { ELEMENT: string }) {
        if (!this.shouldTakeScreenshots()) {
            return { devicePixelRatio: 1, image: '' };
        }
        const MAX_RETRY_COUNT = 3;
        const SCREENSHOT_RETRY_DELAY = 2000;
        const devicePixelRatioPromise = this.currentDevicePixelRatio ? Promise.resolve(this.currentDevicePixelRatio) : this.getDevicePixelRatio();
        const getElemScreenshot = () => Promise.all([devicePixelRatioPromise, this.driver.takeElementScreenshot(elem)]);
        const [devicePixelRatio, image] = await pRetry(getElemScreenshot, { retries: MAX_RETRY_COUNT, minTimeout: SCREENSHOT_RETRY_DELAY, factor: 1 });
        const base64 = image || '';
        const dataUrl = `data:image/png;base64,${this.base64AddPadding(base64.replace(/[\r\n]/g, ''))}`;
        return {
            image: dataUrl,
            devicePixelRatio,
        };
    }

    private async getDevicePixelRatio() {
        function devicePixelRatioJS() {
            try {
                return window.devicePixelRatio;
            } catch (err) {
                return 1;
            }
        }

        const { value } = await this.driver.executeJS(devicePixelRatioJS);
        return value;
    }

    public forcePixelRatio(forceRatio = 1) {
        this.currentDevicePixelRatio = forceRatio;
        return Promise.resolve();
    }

    public getCurrentDevicePixelRatio() {
        return this.currentDevicePixelRatio;
    }
}
